/**
 * RestockAlertsPage - Page dédiée aux alertes de réapprovisionnement
 *
 * Accessible depuis le POS pour consulter :
 * - Alertes de réapprovisionnement rayon (SHELF_EMPTY, SHELF_LOW)
 * - Suggestions de commandes fournisseur (ORDER_SUGGESTED)
 * - Promos DLC courte : le pilotage anti-gaspi du tableau de bord, à portée de la
 *   caisse (21/10/2026)
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import api from '../../services/api';
import { formatQty } from '../../utils/pos.utils';
import ProductEditModal from '../../components/ProductEditModal';
import ScanShelveBar from '../../components/pos/ScanShelveBar';
import BilanSeriePrecedente, { ResumeSerie } from '../../components/pos/BilanSeriePrecedente';
import { useScanShelve } from '../../hooks/useScanShelve';
import { useBarcodeScan } from '../../hooks/useBarcodeScan';
import { Product } from '../../types/pos.types';
import {
  ArrowLeft,
  Package,
  ShoppingCart,
  Download,
  RefreshCw,
  CheckCircle,
  ArrowUpFromLine,
  Edit,
  AlertTriangle,
  Clock,
  MapPin,
  Tag,
} from 'lucide-react';
import PromoDlcPage, { type CandidatPromo } from '../PromoDlcPage';

interface RestockAlert {
  id: string;
  product_id: string;
  type: 'SHELF_EMPTY' | 'SHELF_LOW' | 'ORDER_SUGGESTED';
  status: 'PENDING' | 'RESOLVED';
  stock_display: number;
  stock_backroom: number;
  stock_total: number | null;
  threshold: number | null;
  /**
   * Rayon plein : ce qui tient sur l'étal. Saisi à la main ('manual'), déduit
   * des gestes de la semaine ('deduced'), ou — faute des deux — emprunté à la
   * médiane des voisins de rayon ('voisins'), qui n'est jamais écrite en base.
   */
  shelf_capacity: number | null;
  shelf_capacity_source: string | null;
  /** Où la marchandise dort en réserve (rangement chaotique). */
  storage_locations: string | null;
  /** Le nombre à lire : combien mettre en rayon maintenant. */
  to_add: number;
  created_at: string;
  product: {
    id: string;
    name: string;
    code: string | null;
    barcode: string | null;
    image_url: string | null;
    supplier_name: string | null;
  };
}

interface OrderSuggestion {
  product_id: string;
  type: 'ORDER_SUGGESTED';
  stock_total: number;
  threshold: number;
  last_out_at: string | null;
  product: {
    id: string;
    name: string;
    code: string | null;
    barcode: string | null;
    image_url: string | null;
    supplier_name: string | null;
    /** Rayon (catégorie de tête) : permet d'isoler l'épicerie du frais. */
    rayon: string | null;
    /** Marque / producteur (Cook, Clearspring…). */
    producer_name?: string | null;
  };
  /** Fiches semblables qui ont encore du stock : à voir avant de recommander. */
  proches?: Array<{ id: string; name: string; stock_total: number }>;
  /** Bilan de la dernière série reçue (vendu, perdu, marge). */
  serie_precedente?: ResumeSerie | null;
  /**
   * Garde-fou épicerie sèche (27/10/2026) : rythme de vente, mois de stock,
   * quantité à commander au plus ; 'surstock' = plus de 6 mois de stock ou rien
   * vendu en 90 j, signalé EN ROUGE sans être retiré ; 'en_test' = produit reçu
   * il y a moins de 90 j.
   */
  couverture?: {
    statut: 'commander' | 'en_test' | 'surstock';
    ventes_par_mois: number | null;
    mois_de_stock: number | null;
    quantite_max: number | null;
    motif: string | null;
  };
}

interface AlertCounts {
  total: number;
  shelf_empty: number;
  shelf_low: number;
  order_suggested: number;
  withdrawn: number;
}

interface WithdrawnItem {
  lot_id: string;
  lot_number: string;
  product_id: string;
  product_name: string;
  image_url: string | null;
  stock_display: number;
  stock_backroom: number;
  total: number;
  unit: string | null;
  expiry_date: string | null;
  /** Ce qui a deja ete sorti de CE lot (pertes, prelevements). */
  deja_sorti?: number;
  derniere_sortie?: string | null;
  reception_date: string | null;
  status: string;
  reasons: string[];
}

export default function RestockAlertsPage() {
  const navigate = useNavigate();
  const { tenant } = useTenant();

  const [alerts, setAlerts] = useState<RestockAlert[]>([]);
  const [orderSuggestions, setOrderSuggestions] = useState<OrderSuggestion[]>([]);
  const [withdrawnItems, setWithdrawnItems] = useState<WithdrawnItem[]>([]);
  const [counts, setCounts] = useState<AlertCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'shelf' | 'orders' | 'withdrawn' | 'promos'>('shelf');
  // Produits à DLC courte pas encore passés en promo — la pastille de l'onglet.
  const [promosAFaire, setPromosAFaire] = useState(0);
  // Relancer le chargement de l'onglet Promos depuis le bouton ↻ de l'en-tête.
  const [clePromos, setClePromos] = useState(0);
  const compterPromos = (candidats: CandidatPromo[]) =>
    setPromosAFaire(candidats.filter((c) => !c.promo_active).length);
  const [exporting, setExporting] = useState(false);

  // Fiche produit ouverte depuis une alerte
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editProduct, setEditProduct] = useState<Product | null>(null);

  // Mode « scan = mise en rayon » : on scanne l'article qu'on vient de remettre
  // en rayon, l'alerte correspondante disparaît ensuite d'elle-même (findAll
  // recalcule le stock réel à chaque affichage et archive les alertes sans objet).
  const scan = useScanShelve({
    onShelved: () => {
      loadAlerts(true);
      loadCounts();
    },
  });

  // Le scan clavier (douchette) n'est écouté QUE mode armé : hors mode, cette page
  // n'a jamais capté les scans, on ne change rien à son comportement.
  useBarcodeScan({ onScan: scan.handleScan, enabled: scan.armed, wedgeOnTouch: scan.wedgeOnTouch });

  useEffect(() => {
    if (tenant?.id) {
      loadAlerts();
      loadCounts();
      api
        .get<CandidatPromo[]>('/products/promo-dlc/candidates')
        .then((c) => compterPromos(Array.isArray(c) ? c : []))
        .catch(() => undefined);
    }
  }, [tenant?.id]);

  // `silent` : recharge sans repasser par l'écran de chargement — sinon la liste
  // clignote après chaque salve de scans.
  const loadAlerts = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [shelfData, orderData, withdrawnData] = await Promise.all([
        api.get<RestockAlert[]>('/pos/restock-alerts'),
        api.get<OrderSuggestion[]>('/pos/restock-alerts/order-suggestions'),
        api.get<WithdrawnItem[]>('/pos/restock-alerts/withdrawn-with-stock'),
      ]);
      setAlerts(shelfData);
      setOrderSuggestions(orderData);
      setWithdrawnItems(withdrawnData);
    } catch (error) {
      console.error('Erreur chargement alertes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCounts = async () => {
    try {
      const data = await api.get<AlertCounts>('/pos/restock-alerts/count');
      setCounts(data);
    } catch (error) {
      console.error('Erreur chargement compteurs:', error);
    }
  };

  /**
   * Noter combien tient sur l'étal, devant le rayon.
   *
   * C'est là que l'information existe : l'opératrice voit que « ajouter 6 » est
   * faux parce qu'il n'y a la place que pour 4. Une valeur saisie prime sur
   * l'observation nocturne et n'est plus jamais écrasée.
   */
  const handleSetCapacity = async (productId: string, capacity: number | null) => {
    try {
      await api.patch(`/pos/restock-alerts/product/${productId}/shelf-capacity`, { capacity });
      await loadAlerts();
    } catch (error) {
      console.error('Erreur enregistrement du rayon plein:', error);
    }
  };

  /**
   * « Pas celui-là cette semaine. » La ligne se tait jusqu'au prochain jeudi
   * 14 h — quand les rayons sont refaits et que la question se repose.
   */
  const handleSnooze = async (productId: string, nom: string) => {
    if (!window.confirm(`Ne plus proposer « ${nom} » jusqu'au prochain réassort ?`)) return;
    try {
      await api.post(`/pos/restock-alerts/product/${productId}/snooze`);
      await loadAlerts();
      await loadCounts();
    } catch (error) {
      console.error('Erreur mise en sommeil:', error);
    }
  };

  const handleReactivate = async (item: WithdrawnItem) => {
    const isExpired = item.reasons.includes('expired');
    // Corps envoyé : pour un produit SEULEMENT désactivé (DLC valide), on NE renvoie PAS
    // expiry_date → le backend ne touche pas à la DLC (évite d'effacer une date valide).
    let body: { expiry_date?: string } = {};

    if (isExpired) {
      const current = item.expiry_date
        ? new Date(item.expiry_date).toLocaleDateString('fr-FR')
        : 'aucune';
      const input = window.prompt(
        `Réactiver « ${item.product_name} » (${formatQty(item.total)} en stock).\n` +
          `DLC actuelle (périmée) : ${current}.\n\n` +
          `Saisis la VRAIE DLC au format JJ/MM/AAAA :`,
        '',
      );
      if (input === null) return; // annulé
      if (!input.trim()) {
        alert('Indique la vraie DLC pour réactiver un produit marqué périmé.');
        return;
      }
      const m = input.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (!m) {
        alert('Format de date invalide. Utilise JJ/MM/AAAA (ex : 30/01/2027).');
        return;
      }
      const iso = `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
      // Garde-fou date réelle (le regex accepte 31/02 → décalage silencieux sinon)
      const d = new Date(`${iso}T00:00:00Z`);
      if (
        d.getUTCFullYear() !== Number(m[3]) ||
        d.getUTCMonth() + 1 !== Number(m[2]) ||
        d.getUTCDate() !== Number(m[1])
      ) {
        alert(`Cette date n'existe pas (${input.trim()}). Vérifie le jour/mois.`);
        return;
      }
      body = { expiry_date: iso };
    } else {
      // Produit désactivé uniquement → simple confirmation, DLC inchangée
      if (
        !window.confirm(
          `Réactiver « ${item.product_name} » (${formatQty(item.total)} en stock) ?\n` +
            `La DLC n'est pas modifiée.`,
        )
      )
        return;
    }

    try {
      await api.post(`/pos/restock-alerts/withdrawn/${item.lot_id}/reactivate`, body);
      await loadAlerts();
      await loadCounts();
    } catch (error) {
      console.error('Erreur réactivation lot:', error);
      alert('Erreur lors de la réactivation');
    }
  };

  const handleWriteOff = async (item: WithdrawnItem) => {
    if (
      !window.confirm(
        `Sortir en PERTE « ${item.product_name} » (${formatQty(item.total)} ${item.unit || ''}) ?\n\n` +
          `Le stock rayon + réserve sera mis à 0 (motif : DLC dépassée). Action tracée.`,
      )
    )
      return;
    try {
      await api.post(`/pos/restock-alerts/withdrawn/${item.lot_id}/write-off`, {});
      await loadAlerts();
      await loadCounts();
    } catch (error) {
      console.error('Erreur sortie en perte:', error);
      alert('Erreur lors de la sortie en perte');
    }
  };

  // Alerte rayon → Réception, onglet Mise en rayon, recherche pré-remplie sur le produit.
  // On préfère le code-barres puis le code (uniques) au nom, qui peut matcher plusieurs
  // fiches. Le filtre de l'onglet cherche dans nom / code / code-barres / n° de lot.
  const handleMoveToDisplay = (alert: RestockAlert) => {
    const q = alert.product.barcode || alert.product.code || alert.product.name;
    navigate(`/pos/reception?tab=rayon&q=${encodeURIComponent(q)}`);
  };

  // Fiche produit ouverte sur place : on ne quitte pas la liste d'alertes.
  const handleEditProduct = async (productId: string) => {
    try {
      const product = await api.getProduct(productId);
      setEditProduct(product);
      setEditModalOpen(true);
    } catch (error) {
      console.error('Erreur chargement produit:', error);
      alert('Erreur lors du chargement de la fiche produit');
    }
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      // Utiliser l'API get pour récupérer le CSV comme texte
      const csvContent = await api.get<string>('/pos/restock-alerts/export', {
        responseType: 'text',
      });

      // Créer un blob et télécharger
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `commandes-suggerees-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur export CSV:', error);
    } finally {
      setExporting(false);
    }
  };

  /**
   * Les plus gros trous d'abord. On veut les rayons pleins à 100 % — c'est aussi
   * ainsi qu'un vol se voit —, donc rien n'est masqué : un « ajouter 1 » compte.
   * Mais commencer par les vingt manquants plutôt que par l'unité isolée fait
   * gagner l'essentiel dès les premières minutes.
   */
  const shelfAlerts = alerts
    .filter((a) => a.type === 'SHELF_EMPTY' || a.type === 'SHELF_LOW')
    .sort((a, b) => (b.to_add ?? 0) - (a.to_add ?? 0));

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Alertes de réapprovisionnement
                </h1>
                <p className="text-sm text-gray-500">
                  {counts?.total || 0} alerte{(counts?.total || 0) > 1 ? 's' : ''} en attente
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                loadAlerts();
                loadCounts();
                if (activeTab === 'promos') setClePromos((k) => k + 1);
              }}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 pt-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-white rounded-lg p-1 shadow-sm">
          <button
            onClick={() => setActiveTab('shelf')}
            className={`flex items-center justify-center gap-2 py-3 px-4 min-h-[56px] rounded-lg text-lg font-medium transition-colors ${
              activeTab === 'shelf'
                ? 'bg-emerald-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Package className="w-5 h-5" />
            <span>Rayon</span>
            {(counts?.shelf_empty || 0) + (counts?.shelf_low || 0) > 0 && (
              <span
                className={`px-2 py-0.5 text-xs rounded-full ${
                  activeTab === 'shelf'
                    ? 'bg-white/20 text-white'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {(counts?.shelf_empty || 0) + (counts?.shelf_low || 0)}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`flex items-center justify-center gap-2 py-3 px-4 min-h-[56px] rounded-lg text-lg font-medium transition-colors ${
              activeTab === 'orders'
                ? 'bg-emerald-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <ShoppingCart className="w-5 h-5" />
            <span>Commandes</span>
            {(counts?.order_suggested || 0) > 0 && (
              <span
                className={`px-2 py-0.5 text-xs rounded-full ${
                  activeTab === 'orders'
                    ? 'bg-white/20 text-white'
                    : 'bg-orange-100 text-orange-700'
                }`}
              >
                {counts?.order_suggested || 0}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('withdrawn')}
            className={`flex items-center justify-center gap-2 py-3 px-4 min-h-[56px] rounded-lg text-lg font-medium transition-colors ${
              activeTab === 'withdrawn'
                ? 'bg-emerald-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <AlertTriangle className="w-5 h-5" />
            <span>À vérifier</span>
            {(counts?.withdrawn || 0) > 0 && (
              <span
                className={`px-2 py-0.5 text-xs rounded-full ${
                  activeTab === 'withdrawn'
                    ? 'bg-white/20 text-white'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {counts?.withdrawn || 0}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('promos')}
            className={`flex items-center justify-center gap-2 py-3 px-4 min-h-[56px] rounded-lg text-lg font-medium transition-colors ${
              activeTab === 'promos'
                ? 'bg-emerald-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Tag className="w-5 h-5" />
            <span>Promos DLC</span>
            {promosAFaire > 0 && (
              <span
                className={`px-2 py-0.5 text-xs rounded-full ${
                  activeTab === 'promos'
                    ? 'bg-white/20 text-white'
                    : 'bg-amber-100 text-amber-800'
                }`}
              >
                {promosAFaire}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Mode « scan = mise en rayon » — proposé sur l'onglet Rayon, mais le bandeau
          reste visible sur les autres onglets tant que le mode est armé : il déplace
          du stock, il ne doit jamais tourner sans qu'on le voie. */}
      {(activeTab === 'shelf' || scan.armed) && (
        <div className="max-w-7xl mx-auto px-4 pt-4">
          <ScanShelveBar scan={scan} />
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        {activeTab === 'promos' ? (
          <PromoDlcPage key={clePromos} integre onCandidats={compterPromos} />
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : activeTab === 'shelf' ? (
          <ShelfAlertsSection
            alerts={shelfAlerts}
            formatDate={formatDate}
            onMoveToDisplay={handleMoveToDisplay}
            onEditProduct={handleEditProduct}
            onSetCapacity={handleSetCapacity}
            onSnooze={handleSnooze}
          />
        ) : activeTab === 'orders' ? (
          <OrderAlertsSection
            suggestions={orderSuggestions}
            onExport={handleExportCSV}
            exporting={exporting}
            formatDate={formatDate}
          />
        ) : (
          <WithdrawnSection
            items={withdrawnItems}
            onReactivate={handleReactivate}
            onWriteOff={handleWriteOff}
          />
        )}
      </div>

      {/* Fiche produit — l'enregistrement peut changer le stock, on recharge les alertes */}
      <ProductEditModal
        product={editProduct}
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditProduct(null);
        }}
        onSave={() => {
          loadAlerts();
          loadCounts();
        }}
      />
    </div>
  );
}

interface WithdrawnSectionProps {
  items: WithdrawnItem[];
  onReactivate: (item: WithdrawnItem) => void;
  onWriteOff: (item: WithdrawnItem) => void;
}

function WithdrawnSection({ items, onReactivate, onWriteOff }: WithdrawnSectionProps) {
  const fmt = (d: string | null) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');
  const reasonLabel = (r: string[]) => {
    const parts: string[] = [];
    if (r.includes('expired')) parts.push('Périmé');
    if (r.includes('inactive')) parts.push('Désactivé');
    return parts.join(' · ') || '—';
  };

  if (items.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun produit retiré avec stock</h3>
        <p className="text-gray-500">
          Aucun lot périmé ni produit désactivé ne garde de stock invisible. 👍
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
        <strong>{items.length} produit{items.length > 1 ? 's' : ''}</strong> retiré{items.length > 1 ? 's' : ''} de la vente
        alors qu'{items.length > 1 ? 'ils ont' : 'il a'} encore du stock. Souvent une{' '}
        <strong>coquille de DLC</strong> (corrige la date pour réactiver) — sinon un vrai périmé (sortir en perte).
      </div>
      <div className="bg-white rounded-lg shadow-sm divide-y">
        {items.map((item) => {
          const expired = item.reasons.includes('expired');
          return (
            <div key={item.lot_id} className="p-4 flex items-center gap-3 flex-wrap">
              {item.image_url ? (
                <img src={item.image_url} alt="" className="w-12 h-12 object-cover rounded-lg border" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">
                  <Package className="w-6 h-6" />
                </div>
              )}
              <div className="flex-1 min-w-[180px]">
                <div className="font-medium text-gray-900">{item.product_name}</div>
                <div className="text-xs text-gray-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                  <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold">
                    {reasonLabel(item.reasons)}
                  </span>
                  <span>
                    Rayon : <b>{formatQty(item.stock_display)}</b>
                    {item.stock_backroom > 0 ? ` · Réserve : ${formatQty(item.stock_backroom)}` : ''} {item.unit || ''}
                  </span>
                  <span>
                    DLC : <b className={expired ? 'text-red-600' : ''}>{fmt(item.expiry_date)}</b>
                  </span>
                  {/* Ce qui a DEJA ete sorti de ce lot. Une ligne ne quitte la
                      liste que lorsque le stock du lot tombe a zero : un retrait
                      PARTIEL la laisse en place, et l'ecran, qui n'affichait que
                      le reste, donnait l'impression que rien n'avait ete fait
                      (signale le 21/09/2026). */}
                  {(item.deja_sorti ?? 0) > 0 && (
                    <span className="text-amber-700">
                      déjà sorti : <b>{formatQty(item.deja_sorti as number)}</b>
                      {item.derniere_sortie ? ` le ${fmt(item.derniere_sortie)}` : ''}
                    </span>
                  )}
                  <span>Reçu : {fmt(item.reception_date)}</span>
                  <span className="text-gray-400">{item.lot_number}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => onReactivate(item)}
                  className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-lg font-medium min-h-[48px]"
                >
                  Corriger DLC / réactiver
                </button>
                <button
                  onClick={() => onWriteOff(item)}
                  className="px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 text-sm rounded-lg font-medium min-h-[48px]"
                >
                  Sortir en perte
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface ShelfAlertsSectionProps {
  alerts: RestockAlert[];
  formatDate: (date: string) => string;
  onMoveToDisplay: (alert: RestockAlert) => void;
  onEditProduct: (productId: string) => void;
  onSetCapacity: (productId: string, capacity: number | null) => void;
  onSnooze: (productId: string, nom: string) => void;
}

function ShelfAlertsSection({
  alerts,
  formatDate,
  onMoveToDisplay,
  onEditProduct,
  onSetCapacity,
  onSnooze,
}: ShelfAlertsSectionProps) {
  const emptyAlerts = alerts.filter((a) => a.type === 'SHELF_EMPTY');
  const lowAlerts = alerts.filter((a) => a.type === 'SHELF_LOW');

  if (alerts.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Aucune alerte de réapprovisionnement
        </h3>
        <p className="text-gray-500">
          Tous les produits ont suffisamment de stock en rayon.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Rayon vide */}
      {emptyAlerts.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-red-700 mb-3 flex items-center gap-2">
            <span className="w-3 h-3 bg-red-500 rounded-full"></span>
            Rayon vide ({emptyAlerts.length})
          </h2>
          <div className="bg-white rounded-lg shadow-sm divide-y">
            {emptyAlerts.map((alert) => (
              <AlertRow
                key={alert.id}
                alert={alert}
                severity="high"
                formatDate={formatDate}
                onMoveToDisplay={() => onMoveToDisplay(alert)}
                onEditProduct={() => onEditProduct(alert.product_id)}
                onSetCapacity={onSetCapacity}
                onSnooze={onSnooze}
              />
            ))}
          </div>
        </div>
      )}

      {/* Stock bas */}
      {lowAlerts.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-orange-700 mb-3 flex items-center gap-2">
            <span className="w-3 h-3 bg-orange-500 rounded-full"></span>
            Stock rayon bas ({lowAlerts.length})
          </h2>
          <div className="bg-white rounded-lg shadow-sm divide-y">
            {lowAlerts.map((alert) => (
              <AlertRow
                key={alert.id}
                alert={alert}
                severity="medium"
                formatDate={formatDate}
                onMoveToDisplay={() => onMoveToDisplay(alert)}
                onEditProduct={() => onEditProduct(alert.product_id)}
                onSetCapacity={onSetCapacity}
                onSnooze={onSnooze}
              />
            ))}
          </div>
        </div>
      )}

      <p className="text-sm text-gray-500 text-center">
        « Mise en rayon » ouvre la réception sur le produit ; le crayon ouvre sa fiche.
      </p>
    </div>
  );
}

interface OrderAlertsSectionProps {
  suggestions: OrderSuggestion[];
  onExport: () => void;
  exporting: boolean;
  formatDate: (date: string) => string;
}

// Normalisation pour la recherche (minuscules, sans accents)
function normalizeSearch(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Rythme et plafond d'une ligne d'épicerie sèche : « se vend 2/mois · 1,5 mois
 * de stock · à commander au plus 10 », ou « en test » pour un produit récent.
 */
function LigneCouverture({ c }: { c: NonNullable<OrderSuggestion['couverture']> }) {
  if (c.statut === 'en_test') {
    return (
      <div className="mt-1.5 inline-flex flex-wrap items-center gap-1 rounded-md bg-sky-50 border border-sky-200 px-2 py-1 text-xs text-sky-900">
        <span className="font-semibold">🧪 En test</span>
        <span>{c.motif?.replace(/^en test /, '')}</span>
        {c.ventes_par_mois !== null && <span>· se vend {formatQty(c.ventes_par_mois)}/mois</span>}
      </div>
    );
  }
  if (c.statut === 'surstock') {
    // Philippe (27/10/2026) : « pas exclure mais informer en rouge »
    return (
      <div className="mt-1.5 rounded-md bg-red-50 border border-red-300 px-2 py-1 text-xs text-red-800">
        <span className="font-semibold">⚠️ Pas besoin de commander : {c.motif}</span>
        {c.ventes_par_mois !== null && c.ventes_par_mois > 0 && (
          <> · se vend {formatQty(c.ventes_par_mois)}/mois</>
        )}
      </div>
    );
  }
  if (c.ventes_par_mois === null) return null;
  return (
    <div className="mt-1.5 text-xs text-gray-600">
      Se vend <span className="font-semibold tabular-nums">{formatQty(c.ventes_par_mois)}</span>/mois
      {c.mois_de_stock !== null && <> · {formatQty(c.mois_de_stock)} mois de stock</>}
      {c.quantite_max !== null && (
        <>
          {' · '}
          <span className="font-semibold text-emerald-800">à commander au plus {formatQty(c.quantite_max)}</span>
        </>
      )}
    </div>
  );
}

function OrderAlertsSection({
  suggestions,
  onExport,
  exporting,
  formatDate,
}: OrderAlertsSectionProps) {
  const [selectedSupplier, setSelectedSupplier] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [rayonsExclus, setRayonsExclus] = useState<string[]>([]);

  if (suggestions.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Aucune commande suggérée
        </h3>
        <p className="text-gray-500">
          Tous les produits ont un stock total suffisant.
        </p>
      </div>
    );
  }

  // Rayons écartés de la liste. Commander chez un fournisseur mixte comme
  // Interbio, c'est deux gestes distincts : le frais se commande au jour le jour,
  // l'épicerie au réassort. Les mélanger rend la liste inexploitable.
  const rayonsPresents = Array.from(
    new Set(suggestions.map((s) => s.product.rayon || 'Sans rayon')),
  ).sort((a, b) => a.localeCompare(b, 'fr'));

  const filteredParRayon = suggestions.filter(
    (s) => !rayonsExclus.includes(s.product.rayon || 'Sans rayon'),
  );

  // Filtre de recherche (nom, marque, code, code-barres) — avant le groupement fournisseur
  const query = normalizeSearch(search.trim());
  const filteredSuggestions = query
    ? filteredParRayon.filter((s) =>
        [s.product.name, s.product.producer_name, s.product.code, s.product.barcode].some(
          (field) => field && normalizeSearch(field).includes(query),
        ),
      )
    : filteredParRayon;

  // Grouper par fournisseur (l'ordre « récemment épuisé » est conservé dans chaque groupe)
  const bySupplier = filteredSuggestions.reduce((acc, s) => {
    const supplier = s.product.supplier_name || 'Sans fournisseur';
    if (!acc[supplier]) acc[supplier] = [];
    acc[supplier].push(s);
    return acc;
  }, {} as Record<string, OrderSuggestion[]>);

  // Liste des fournisseurs triée (« Sans fournisseur » en dernier)
  const supplierNames = Object.keys(bySupplier).sort((a, b) => {
    if (a === 'Sans fournisseur') return 1;
    if (b === 'Sans fournisseur') return -1;
    return a.localeCompare(b, 'fr');
  });

  // Le filtre sélectionné peut avoir disparu après rafraîchissement → fallback "all"
  const activeSupplier =
    selectedSupplier !== 'all' && !bySupplier[selectedSupplier]
      ? 'all'
      : selectedSupplier;

  const visibleSuppliers =
    activeSupplier === 'all' ? supplierNames : [activeSupplier];

  return (
    <div className="space-y-4">
      {/* Recherche + sélecteur fournisseur + export */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="🔍 Rechercher un produit…"
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white w-56 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <label htmlFor="supplier-filter" className="text-sm font-medium text-gray-600">
            Fournisseur
          </label>
          <select
            id="supplier-filter"
            value={activeSupplier}
            onChange={(e) => setSelectedSupplier(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value="all">Tous ({filteredSuggestions.length})</option>
            {supplierNames.map((name) => (
              <option key={name} value={name}>
                {name} ({bySupplier[name].length})
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={onExport}
          disabled={exporting}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
        >
          <Download className={`w-4 h-4 ${exporting ? 'animate-bounce' : ''}`} />
          <span>Exporter CSV</span>
        </button>
      </div>

      {/* Rayons. Un fournisseur mixte comme Interbio livre le frais ET l'épicerie,
          qui ne se commandent pas au même rythme : écarter les rayons frais isole
          la liste d'épicerie. Un clic bascule le rayon, il n'y a rien à valider. */}
      {rayonsPresents.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-600">Rayons</span>
          {rayonsPresents.map((r) => {
            const exclu = rayonsExclus.includes(r);
            const n = suggestions.filter((s) => (s.product.rayon || 'Sans rayon') === r).length;
            return (
              <button
                key={r}
                onClick={() =>
                  setRayonsExclus((prev) =>
                    prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r],
                  )
                }
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border min-h-[48px] transition-colors ${
                  exclu
                    ? 'bg-gray-100 border-gray-300 text-gray-400 line-through'
                    : 'bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100'
                }`}
                title={exclu ? 'Réintégrer ce rayon' : 'Écarter ce rayon de la liste'}
              >
                {r} ({n})
              </button>
            );
          })}
          {rayonsExclus.length > 0 && (
            <button
              onClick={() => setRayonsExclus([])}
              className="px-3 py-1.5 text-xs text-gray-600 underline min-h-[48px]"
            >
              tout réafficher
            </button>
          )}
        </div>
      )}

      {/* Aucun résultat pour la recherche */}
      {query && filteredSuggestions.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">
          Aucun produit à commander ne correspond à « {search.trim()} ».
        </div>
      )}

      {/* Liste par fournisseur */}
      {visibleSuppliers.map((supplier) => {
        const supplierSuggestions = bySupplier[supplier];
        return (
        <div key={supplier}>
          <h2 className="text-lg font-semibold text-gray-700 mb-3">
            {supplier} ({supplierSuggestions.length})
          </h2>
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Produit
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Stock actuel
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Seuil
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Dernière sortie
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {supplierSuggestions.map((s) => {
                  const depleted = s.stock_total <= 0;
                  return (
                    <tr key={s.product_id} className="hover:bg-gray-50 align-top">
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-3">
                          {/* La photo et la marque : on reconnaît l'article d'un coup
                              d'œil, avant de le confondre avec son voisin de rayon. */}
                          {s.product.image_url ? (
                            <img
                              src={s.product.image_url}
                              alt=""
                              loading="lazy"
                              className="w-14 h-14 flex-shrink-0 object-contain rounded-lg border border-gray-200 bg-white"
                            />
                          ) : (
                            <div className="w-14 h-14 flex-shrink-0 rounded-lg border border-dashed border-gray-300 bg-gray-50" />
                          )}
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                              {s.product.name}
                              {depleted && (
                                <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded bg-red-100 text-red-700">
                                  Épuisé
                                </span>
                              )}
                            </div>
                            {(s.product.producer_name || s.product.code) && (
                              <div className="text-xs text-gray-500">
                                {s.product.producer_name && (
                                  <span className="font-semibold text-gray-700">{s.product.producer_name}</span>
                                )}
                                {s.product.producer_name && s.product.code && ' · '}
                                {s.product.code}
                              </div>
                            )}
                            {/* Fiches semblables qui ont ENCORE du stock : le piège
                                des erreurs de commande (Philippe, 05/10/2026). */}
                            {s.proches && s.proches.length > 0 && (
                              <div className="mt-1.5 rounded-md bg-amber-50 border border-amber-200 px-2 py-1 text-xs text-amber-900">
                                <span className="font-semibold">Encore en stock, proche :</span>{' '}
                                {s.proches.map((p, i) => (
                                  <span key={p.id}>
                                    {i > 0 && ' · '}
                                    {p.name} <span className="font-semibold tabular-nums">({formatQty(p.stock_total)})</span>
                                  </span>
                                ))}
                              </div>
                            )}
                            {s.couverture && <LigneCouverture c={s.couverture} />}
                            {s.serie_precedente && <BilanSeriePrecedente serie={s.serie_precedente} />}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`font-semibold ${
                            depleted ? 'text-red-600' : 'text-orange-600'
                          }`}
                        >
                          {s.stock_total}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-gray-500">
                        {s.threshold}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-gray-500">
                        {s.last_out_at ? formatDate(s.last_out_at) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        );
      })}
    </div>
  );
}

interface AlertRowProps {
  alert: RestockAlert;
  severity: 'high' | 'medium';
  formatDate: (date: string) => string;
  onMoveToDisplay: () => void;
  onEditProduct: () => void;
  onSetCapacity: (productId: string, capacity: number | null) => void;
  onSnooze: (productId: string, nom: string) => void;
}

function AlertRow({
  alert,
  severity,
  formatDate,
  onMoveToDisplay,
  onEditProduct,
  onSetCapacity,
  onSnooze,
}: AlertRowProps) {
  const bgColor = severity === 'high' ? 'hover:bg-red-50' : 'hover:bg-orange-50';
  const textColor = severity === 'high' ? 'text-red-600' : 'text-orange-600';
  // Saisie du rayon plein : on garde le texte tel quel tant qu'on tape (une
  // valeur contrôlée par le nombre effacerait le champ dès la première touche).
  const [capaSaisie, setCapaSaisie] = useState<string>(
    alert.shelf_capacity != null ? String(alert.shelf_capacity) : '',
  );
  const capaModifiee =
    capaSaisie.trim() !== (alert.shelf_capacity != null ? String(alert.shelf_capacity) : '');

  const validerCapacite = () => {
    const brut = capaSaisie.trim().replace(',', '.');
    if (brut === '') return onSetCapacity(alert.product_id, null);
    const valeur = parseFloat(brut);
    if (!Number.isFinite(valeur) || valeur < 0) return;
    onSetCapacity(alert.product_id, valeur);
  };

  return (
    <div className={`p-4 ${bgColor} transition-colors`}>
      {/* Sur téléphone (PWA en rayon), les actions passent SOUS l'article : les trois
          boutons + le nom ne tiennent pas sur une ligne de 375 px. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900">{alert.product.name}</span>
            {alert.product.code && (
              <span className="text-xs text-gray-500">({alert.product.code})</span>
            )}
          </div>
          <div className="flex items-center gap-4 mt-1 text-sm flex-wrap">
            {/* Le nombre qu'on lit sans réfléchir : combien en mettre, maintenant. */}
            {alert.to_add > 0 && (
              <span className="px-2 py-0.5 rounded-md bg-emerald-600 text-white font-bold">
                Ajouter {formatQty(alert.to_add)}
              </span>
            )}
            <span className={textColor}>
              Rayon: {formatQty(alert.stock_display)}
              {alert.shelf_capacity != null
                ? ` / ${alert.shelf_capacity_source === 'voisins' ? '≈' : ''}${formatQty(alert.shelf_capacity)}`
                : ''}
            </span>
            <span className="text-emerald-600">
              Réserve: {formatQty(alert.stock_backroom)}
            </span>
            <span className="text-gray-400">
              {formatDate(alert.created_at)}
            </span>
          </div>

          {/* Où aller chercher. Le rangement chaotique n'attribue pas de place
              fixe par produit : savoir qu'il faut six bocaux ne dit pas dans
              quelle allée ils dorment. */}
          {alert.storage_locations && (
            <div className="flex items-center gap-1.5 mt-1 text-sm text-purple-700">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span className="font-medium">{alert.storage_locations}</span>
            </div>
          )}

          {/* Rayon plein : mesuré la nuit d'après les gestes, corrigeable ici —
              devant l'étal, seul endroit où l'on sait vraiment ce qui tient. */}
          <div className="flex items-center gap-2 mt-2 text-sm">
            <label className="text-gray-500">Rayon plein</label>
            <input
              type="text"
              inputMode="decimal"
              value={capaSaisie}
              onChange={(e) => setCapaSaisie(e.target.value)}
              onBlur={() => capaModifiee && validerCapacite()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur();
                }
              }}
              placeholder="—"
              className="w-16 px-2 py-1 border border-gray-300 rounded-md text-center focus:outline-none focus:ring-2 focus:ring-emerald-400"
            />
            {alert.shelf_capacity_source === 'deduced' && (
              <span className="text-xs text-gray-400">déduit des mises en rayon</span>
            )}
            {alert.shelf_capacity_source === 'manual' && (
              <span className="text-xs text-blue-500">saisi</span>
            )}
            {alert.shelf_capacity_source === 'voisins' && (
              <span className="text-xs text-amber-600">≈ d'après les voisins de rayon</span>
            )}
            {(capaModifiee || alert.shelf_capacity_source === 'voisins') && (
              <button
                onClick={validerCapacite}
                className="text-xs font-semibold text-emerald-700 hover:underline"
              >
                {capaModifiee ? 'enregistrer' : 'confirmer'}
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Mise en rayon — l'action des vendeurs. Mise en avant (vert plein) quand il
              y a effectivement du stock en réserve à descendre ; sinon il n'y a rien à
              transférer et c'est la fiche (ou une commande) qu'il faut ouvrir. */}
          <button
            onClick={onMoveToDisplay}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors min-h-[48px] ${
              alert.stock_backroom > 0
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : 'text-emerald-700 border border-emerald-300 hover:bg-emerald-50'
            }`}
            title={
              alert.stock_backroom > 0
                ? `Mise en rayon — ${formatQty(alert.stock_backroom)} en réserve`
                : 'Mise en rayon (rien en réserve pour ce produit)'
            }
          >
            <ArrowUpFromLine className="w-4 h-4" />
            Mise en rayon
          </button>
          {/* Fiche produit — ouverte sur place, on ne quitte pas la liste d'alertes. */}
          <button
            onClick={onEditProduct}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors min-h-[48px]"
            title="Modifier la fiche produit"
          >
            <Edit className="w-5 h-5" />
          </button>
          {/* « Pas celui-là cette semaine » : la ligne se tait jusqu'au prochain
              jeudi 14 h. Sans ce bouton, un produit qu'on a décidé de ne pas
              remettre revient chaque jour et use la confiance dans la liste. */}
          <button
            onClick={() => onSnooze(alert.product_id, alert.product.name)}
            className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors min-h-[48px]"
            title="Pas celui-là cette semaine"
          >
            <Clock className="w-5 h-5" />
          </button>
          {/* « Marquer comme traité » retiré le 26/08/2026. La liste n'est plus
              un journal d'alertes qu'on coche : elle est recalculée à chaque
              affichage depuis le stock réel. Cocher une ligne encore vraie la
              faisait revenir aussitôt ; une ligne devenue fausse disparaissait
              d'elle-même. Deux boutons voisins dont un seul agit, c'est ce qui
              fait perdre confiance dans une liste. Pour taire une ligne, il y a
              « pas celui-là cette semaine », qui agit sur le produit. */}
        </div>
      </div>
    </div>
  );
}
